import { CartAddEvent, CartErrorEvent } from '@theme/events';

const moneyText = (variant) => variant?.price_money || '';

const lbpCalendarDate = (year, month, day) => new Date(Date.UTC(year, month - 1, day, 12));

const lbpNextDeliveryDay = (date) => {
  const next = new Date(date);
  do {
    next.setUTCDate(next.getUTCDate() + 1);
  } while (next.getUTCDay() === 0);
  return next;
};

const lbpAddDeliveryDays = (date, amount) => {
  let result = new Date(date);
  for (let added = 0; added < amount;) {
    result.setUTCDate(result.getUTCDate() + 1);
    if (result.getUTCDay() !== 0) added += 1;
  }
  return result;
};

function initDeliveryTimeline(root) {
  const timeline = root.querySelector('[data-lbp-delivery-timeline]');
  if (!timeline || timeline.dataset.lbpReady === 'true') return;
  timeline.dataset.lbpReady = 'true';

  const amsterdamParts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Amsterdam',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date()).reduce((parts, part) => {
    if (part.type !== 'literal') parts[part.type] = Number(part.value);
    return parts;
  }, {});

  const orderedDate = lbpCalendarDate(amsterdamParts.year, amsterdamParts.month, amsterdamParts.day);
  const orderedDay = orderedDate.getUTCDay();
  const orderedBeforeCutoff = orderedDay !== 0 && amsterdamParts.hour < 15;
  let dispatchDate = new Date(orderedDate);
  if (!orderedBeforeCutoff) {
    dispatchDate = lbpNextDeliveryDay(dispatchDate);
  }

  const arrivalStart = lbpAddDeliveryDays(dispatchDate, 1);
  const arrivalEnd = lbpAddDeliveryDays(dispatchDate, 2);
  const locale = timeline.dataset.locale || 'nl-NL';
  const language = locale.toLowerCase().split('-')[0];
  const dayMonth = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' });
  const dayOnly = new Intl.DateTimeFormat(locale, { day: 'numeric', timeZone: 'UTC' });
  const weekday = new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' });
  const isoDate = date => date.toISOString().slice(0, 10);
  const arrivalRange = arrivalStart.getUTCMonth() === arrivalEnd.getUTCMonth()
    && arrivalStart.getUTCFullYear() === arrivalEnd.getUTCFullYear()
    ? `${dayOnly.format(arrivalStart)}\u2013${dayMonth.format(arrivalEnd)}`
    : `${dayMonth.format(arrivalStart)} \u2013 ${dayMonth.format(arrivalEnd)}`;

  const orderedOutput = timeline.querySelector('[data-lbp-order-date]');
  const dispatchOutput = timeline.querySelector('[data-lbp-dispatch-date]');
  const arrivalOutput = timeline.querySelector('[data-lbp-arrival-date]');
  const orderedLabel = timeline.querySelector('[data-lbp-order-label]');
  const dispatchLabel = timeline.querySelector('[data-lbp-dispatch-label]');
  const arrivalLabel = timeline.querySelector('[data-lbp-arrival-label]');
  const deliveryPromise = root.querySelector('[data-lbp-delivery-promise]');
  if (orderedOutput) {
    orderedOutput.dateTime = isoDate(orderedDate);
    orderedOutput.textContent = dayMonth.format(orderedDate);
  }
  if (dispatchOutput) {
    dispatchOutput.dateTime = isoDate(dispatchDate);
    dispatchOutput.textContent = dayMonth.format(dispatchDate);
  }
  if (arrivalOutput) arrivalOutput.textContent = language === 'nl' ? dayMonth.format(arrivalStart) : arrivalRange;

  if (language === 'nl') {
    if (orderedLabel) orderedLabel.textContent = orderedBeforeCutoff ? 'Voor 15:00 besteld' : 'Vandaag besteld';
    if (dispatchLabel) dispatchLabel.textContent = orderedBeforeCutoff ? 'Vandaag verzonden' : `${weekday.format(dispatchDate)} verzonden`;
    if (arrivalLabel) {
      const calendarTomorrow = new Date(orderedDate);
      calendarTomorrow.setUTCDate(calendarTomorrow.getUTCDate() + 1);
      arrivalLabel.textContent = orderedBeforeCutoff && isoDate(arrivalStart) === isoDate(calendarTomorrow)
        ? 'Morgen in huis'
        : `${weekday.format(arrivalStart)} in huis`;
      if (deliveryPromise) {
        deliveryPromise.textContent = orderedBeforeCutoff && isoDate(arrivalStart) === isoDate(calendarTomorrow)
          ? 'Voor 15:00 besteld, morgen in huis'
          : `${orderedBeforeCutoff ? 'Voor 15:00 besteld' : 'Vandaag besteld'}, ${weekday.format(arrivalStart)} in huis`;
      }
    }
  } else if (language === 'de') {
    if (orderedLabel) orderedLabel.textContent = orderedBeforeCutoff ? 'Vor 15:00 Uhr bestellt' : 'Heute bestellt';
    if (dispatchLabel) dispatchLabel.textContent = orderedBeforeCutoff ? 'Heute versendet' : `Versand am ${weekday.format(dispatchDate)}`;
    if (arrivalLabel) arrivalLabel.textContent = 'Lieferzeit 1\u20132 Werktage';
  }
}

function initProductMain(root) {
  if (root.dataset.lbpReady === 'true') return;
  root.dataset.lbpReady = 'true';
  initDeliveryTimeline(root);

  const slides = [...root.querySelectorAll('[data-lbp-slide]')];
  const thumbs = [...root.querySelectorAll('[data-lbp-thumb]')];
  const dots = [...root.querySelectorAll('[data-lbp-dot]')];
  const previous = root.querySelector('[data-lbp-prev]');
  const next = root.querySelector('[data-lbp-next]');
  let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));

  const showMedia = (index) => {
    if (!slides.length) return;
    activeIndex = (index + slides.length) % slides.length;
    slides.forEach((slide, i) => {
      const active = i === activeIndex;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      const video = slide.querySelector('video');
      if (!active && video) video.pause();
      if (active && slide.hasAttribute('data-lbp-animated-image')) {
        const animatedImage = slide.querySelector('img');
        if (animatedImage) animatedImage.replaceWith(animatedImage.cloneNode(true));
      }
    });
    thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('is-active', i === activeIndex);
      thumb.setAttribute('aria-selected', i === activeIndex ? 'true' : 'false');
    });
    dots.forEach((dot, i) => dot.classList.toggle('is-active', i === activeIndex));
    thumbs[activeIndex]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => showMedia(index)));
  dots.forEach((dot, index) => dot.addEventListener('click', () => showMedia(index)));
  previous?.addEventListener('click', () => showMedia(activeIndex - 1));
  next?.addEventListener('click', () => showMedia(activeIndex + 1));

  const stage = root.querySelector('[data-lbp-stage]');
  let pointerStart = null;
  stage?.addEventListener('pointerdown', (event) => { pointerStart = event.clientX; });
  stage?.addEventListener('pointerup', (event) => {
    if (pointerStart === null) return;
    const distance = event.clientX - pointerStart;
    if (Math.abs(distance) > 45) showMedia(activeIndex + (distance < 0 ? 1 : -1));
    pointerStart = null;
  });

  const json = root.querySelector('[data-lbp-product-json]');
  let productData = null;
  try { productData = JSON.parse(json?.textContent || '{}'); } catch (error) { console.warn('LivingBright product data could not be read.', error); }

  const optionInputs = [...root.querySelectorAll('[data-lbp-option]')];
  const hiddenVariant = root.querySelector('input[name="id"]');
  const price = root.querySelector('[data-lbp-price]');
  const compare = root.querySelector('[data-lbp-compare]');
  const saving = root.querySelector('[data-lbp-saving]');
  const stock = root.querySelector('[data-lbp-stock]');
  const stockLabel = stock?.querySelector('[data-lbp-stock-label]');
  const delivery = root.querySelector('[data-lbp-delivery]');
  const deliveryLabel = delivery?.querySelector('[data-lbp-delivery-label]');
  const deliveryPromise = delivery?.querySelector('[data-lbp-delivery-promise]');
  const addButton = root.querySelector('button[name="add"]');
  const addText = addButton?.querySelector('[data-lbp-add-label]');
  const stickyBar = root.querySelector('[data-lbp-sticky-bar]');
  const stickyAddButton = stickyBar?.querySelector('[data-lbp-sticky-add]');
  const stickyAddText = stickyAddButton?.querySelector('span');

  const updateVariant = () => {
    if (!productData?.variants?.length) return;
    const values = [...root.querySelectorAll('[data-lbp-option]:checked')].map((input) => input.value);
    const variant = productData.variants.find((item) => item.options.every((option, index) => option === values[index]));
    root.querySelectorAll('[data-lbp-selected-value]').forEach((label) => {
      const index = Number(label.dataset.lbpSelectedValue);
      label.textContent = values[index] || '';
    });

    if (!variant) {
      if (stockLabel) {
        stockLabel.textContent = productData.unavailable_label;
        stock?.classList.add('is-sold-out');
      } else if (stock) {
        stock.textContent = productData.unavailable_label;
      }
      if (hiddenVariant) hiddenVariant.value = '';
      if (addButton) addButton.disabled = true;
      if (addText) addText.textContent = productData.unavailable_label;
      if (deliveryLabel) deliveryLabel.textContent = productData.delivery_unavailable_label;
      if (deliveryPromise) deliveryPromise.hidden = true;
      delivery?.classList.add('is-sold-out');
      return;
    }

    if (hiddenVariant) {
      hiddenVariant.value = variant.id;
      hiddenVariant.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (price) price.textContent = moneyText(variant);
    if (compare) {
      compare.textContent = variant.compare_money || '';
      compare.hidden = !variant.compare_money || variant.compare_at_price <= variant.price;
    }
    if (saving) {
      const percent = variant.compare_at_price > variant.price
        ? Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100)
        : 0;
      saving.textContent = percent ? `${productData.save_label} ${percent}%` : '';
      saving.hidden = !percent;
    }
    if (stock && stockLabel) {
      if (!variant.available) {
        stockLabel.textContent = productData.sold_out_label;
      } else {
        stockLabel.textContent = productData.in_stock_label;
      }
      stock.classList.toggle('is-sold-out', !variant.available);
    }
    if (deliveryLabel) deliveryLabel.textContent = variant.available ? productData.delivery_available_label : productData.delivery_unavailable_label;
    if (deliveryPromise) deliveryPromise.hidden = !variant.available;
    delivery?.classList.toggle('is-sold-out', !variant.available);
    if (addButton) addButton.disabled = !variant.available;
    if (addText) addText.textContent = variant.available ? productData.add_label : productData.sold_out_label;
    if (stickyAddButton) stickyAddButton.disabled = !variant.available;
    if (stickyAddText) stickyAddText.textContent = variant.available ? productData.add_label : productData.sold_out_label;

    if (variant.featured_media_id) {
      const mediaIndex = slides.findIndex((slide) => Number(slide.dataset.mediaId) === Number(variant.featured_media_id));
      if (mediaIndex >= 0) showMedia(mediaIndex);
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  };

  optionInputs.forEach((input) => input.addEventListener('change', updateVariant));

  root.querySelectorAll('[data-lbp-quantity]').forEach((control) => {
    const input = control.querySelector('input[name="quantity"]');
    control.querySelector('[data-lbp-minus]')?.addEventListener('click', () => {
      input.value = Math.max(Number(input.min || 1), Number(input.value || 1) - 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    control.querySelector('[data-lbp-plus]')?.addEventListener('click', () => {
      const maximum = Number(input.max || 0);
      input.value = maximum ? Math.min(maximum, Number(input.value || 1) + 1) : Number(input.value || 1) + 1;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });

  const formShell = root.querySelector('[data-lbp-form-shell]');
  const form = formShell?.querySelector('form');
  const formError = root.querySelector('[data-lbp-form-error]');
  form?.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!form.checkValidity() || addButton?.disabled) return;

    const originalLabel = addText?.textContent || productData?.add_label || '';
    if (addButton) addButton.disabled = true;
    if (addText) addText.textContent = productData?.adding_label || originalLabel;
    if (stickyAddButton) stickyAddButton.disabled = true;
    if (stickyAddText) stickyAddText.textContent = productData?.adding_label || originalLabel;
    if (formError) formError.hidden = true;

    const formData = new FormData(form);
    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((item) => item.dataset.sectionId)
      .filter(Boolean);
    if (sectionIds.length) formData.set('sections', sectionIds.join(','));

    try {
      const route = window.Theme?.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`;
      const response = await fetch(route, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || productData?.add_error_label || '');

      const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      const cart = cartResponse.ok ? await cartResponse.json() : undefined;
      form.dispatchEvent(new CartAddEvent(cart, String(formData.get('id')), {
        source: 'product-form-component',
        itemCount: Number(formData.get('quantity')) || 1,
        productId: String(productData?.product_id || ''),
        sections: result.sections,
      }));
      if (addText) addText.textContent = productData?.added_label || originalLabel;
      if (stickyAddText) stickyAddText.textContent = productData?.added_label || originalLabel;
      window.setTimeout(() => {
        if (addText) addText.textContent = originalLabel;
        if (stickyAddText) stickyAddText.textContent = originalLabel;
      }, 1800);
      document.dispatchEvent(new CustomEvent('lb:cart:refresh'));
    } catch (error) {
      const message = error instanceof Error ? error.message : (productData?.add_error_label || '');
      if (formError) {
        formError.textContent = message;
        formError.hidden = false;
      }
      form.dispatchEvent(new CartErrorEvent(form.id || 'lb-product-form', message, message));
      if (addText) addText.textContent = originalLabel;
      if (stickyAddText) stickyAddText.textContent = originalLabel;
    } finally {
      const isSoldOut = !hiddenVariant?.value || root.querySelector('[data-lbp-stock]')?.classList.contains('is-sold-out');
      if (addButton) addButton.disabled = isSoldOut;
      if (stickyAddButton) stickyAddButton.disabled = isSoldOut;
    }
  });

}

function initHow(root) {
  if (root.dataset.lbpReady === 'true') return;
  root.dataset.lbpReady = 'true';
  const steps = [...root.querySelectorAll('[data-lbp-how-step]')];
  const visuals = [...root.querySelectorAll('[data-lbp-how-visual]')];
  let currentIndex = 0;
  let timer = null;

  const activate = (index) => {
    currentIndex = index;
    steps.forEach((step, i) => {
      step.classList.toggle('is-active', i === index);
      step.setAttribute('aria-expanded', i === index ? 'true' : 'false');
    });
    visuals.forEach((visual, i) => visual.classList.toggle('is-active', i === index));
  };

  const startAutoAdvance = () => {
    if (timer) window.clearInterval(timer);
    if (steps.length < 2) return;
    timer = window.setInterval(() => {
      activate((currentIndex + 1) % steps.length);
    }, 3000);
  };

  steps.forEach((step, index) => {
    const selectStep = () => {
      activate(index);
      startAutoAdvance();
    };
    step.addEventListener('click', selectStep);
    step.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        selectStep();
      }
    });
  });

  activate(0);
  startAutoAdvance();
}

function initLighting(root) {
  if (root.dataset.lbpReady === 'true') return;
  root.dataset.lbpReady = 'true';
  const tabs = [...root.querySelectorAll('[data-lbp-light-tab]')];
  const images = [...root.querySelectorAll('[data-lbp-light-image]')];
  const activate = (index) => {
    tabs.forEach((tab, i) => {
      tab.classList.toggle('is-active', i === index);
      tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
    });
    images.forEach((image, i) => image.classList.toggle('is-active', i === index));
  };
  tabs.forEach((tab, index) => tab.addEventListener('click', () => activate(index)));
  activate(0);
}

function initCompare(root) {
  if (root.dataset.lbpReady === 'true') return;
  root.dataset.lbpReady = 'true';
  const stage = root.querySelector('.lbp-compare__stage');
  const range = root.querySelector('[data-lbp-compare-range]');
  if (!stage || !range) return;
  const update = () => stage.style.setProperty('--compare-position', `${range.value}%`);
  range.addEventListener('input', update);
  update();
}

function initStickyBar(root) {
  try {
    if (root.dataset.lbpStickyReady === 'true') return;
    root.dataset.lbpStickyReady = 'true';

    const stickyBar = root.querySelector('[data-lbp-sticky-bar]');
    if (!stickyBar) return;

    const addButton = root.querySelector('button[name="add"]');
    const stickyAddButton = stickyBar.querySelector('[data-lbp-sticky-add]');
    const form = root.querySelector('[data-lbp-form-shell] form');

    stickyAddButton?.addEventListener('click', () => {
      if (stickyAddButton.disabled) return;
      try {
        if (typeof form?.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          form?.submit();
        }
      } catch (error) {
        form?.submit();
      }
    });

    if (!addButton) return;

    if (typeof IntersectionObserver === 'function') {
      const stickyObserver = new IntersectionObserver(
        ([entry]) => stickyBar.classList.toggle('is-visible', !entry.isIntersecting),
        { rootMargin: '0px 0px -10% 0px' }
      );
      stickyObserver.observe(addButton);
    } else {
      // Fallback for browsers without IntersectionObserver support.
      const toggleOnScroll = () => {
        const rect = addButton.getBoundingClientRect();
        stickyBar.classList.toggle('is-visible', rect.bottom < 0 || rect.top > window.innerHeight);
      };
      window.addEventListener('scroll', toggleOnScroll, { passive: true });
      toggleOnScroll();
    }
  } catch (error) {
    // Never let a sticky-bar failure block the rest of the product page scripts.
    console.error('LivingBright sticky bar failed to initialize:', error);
  }
}

function initProductHighlights(root) {
  if (root.dataset.lbpHighlightsReady === 'true') return;
  root.dataset.lbpHighlightsReady = 'true';

  const track = root.querySelector('[data-lbp-highlight-track]');
  const cards = [...root.querySelectorAll('.lbp-main__feature')];
  if (!track || cards.length <= 1) return;

  const previousButton = root.querySelector('[data-lbp-highlight-prev]');
  const nextButton = root.querySelector('[data-lbp-highlight-next]');
  const dots = [...root.querySelectorAll('[data-lbp-highlight-dot]')];
  const currentOutput = root.querySelector('[data-lbp-highlight-current]');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const autoplay = root.dataset.lbpHighlightAutoplay === 'true';
  const delay = Number(root.dataset.lbpHighlightDelay) || 2000;
  let timer = null;
  let scrollFrame = null;

  const cardStep = () => {
    const styles = window.getComputedStyle(track);
    return cards[0].getBoundingClientRect().width + (parseFloat(styles.columnGap || styles.gap) || 0);
  };

  const activeIndex = () => Math.max(0, Math.min(cards.length - 1, Math.round(track.scrollLeft / cardStep())));

  const updatePagination = () => {
    const index = activeIndex();
    dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === index));
    if (currentOutput) currentOutput.textContent = String(index + 1);
  };

  const goTo = (index) => {
    const wrappedIndex = (index + cards.length) % cards.length;
    track.scrollTo({ left: cardStep() * wrappedIndex, behavior: reducedMotion ? 'auto' : 'smooth' });
  };

  const move = (direction) => {
    goTo(activeIndex() + direction);
  };

  const stop = () => {
    if (timer) window.clearInterval(timer);
    timer = null;
  };

  const start = () => {
    if (!autoplay || reducedMotion) return;
    stop();
    timer = window.setInterval(() => move(1), delay);
  };

  previousButton?.addEventListener('click', () => {
    move(-1);
    start();
  });
  nextButton?.addEventListener('click', () => {
    move(1);
    start();
  });
  dots.forEach((dot) => dot.addEventListener('click', () => {
    goTo(Number(dot.dataset.lbpHighlightDot));
    start();
  }));
  track.addEventListener('scroll', () => {
    if (scrollFrame) window.cancelAnimationFrame(scrollFrame);
    scrollFrame = window.requestAnimationFrame(updatePagination);
  }, { passive: true });
  root.addEventListener('mouseenter', stop);
  root.addEventListener('mouseleave', start);
  root.addEventListener('focusin', stop);
  root.addEventListener('focusout', start);
  root.addEventListener('touchstart', stop, { passive: true });
  root.addEventListener('touchend', start, { passive: true });

  updatePagination();
  start();
}

function initLivingBrightProducts(scope = document) {
  scope.querySelectorAll('[data-lbp-main]').forEach(initProductMain);
  scope.querySelectorAll('[data-lbp-main]').forEach(initStickyBar);
  scope.querySelectorAll('[data-lbp-highlights]').forEach(initProductHighlights);
  scope.querySelectorAll('[data-lbp-how]').forEach(initHow);
  scope.querySelectorAll('[data-lbp-lighting]').forEach(initLighting);
  scope.querySelectorAll('[data-lbp-compare]').forEach(initCompare);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initLivingBrightProducts());
} else {
  initLivingBrightProducts();
}

document.addEventListener('shopify:section:load', (event) => initLivingBrightProducts(event.target));
